/**
 * Local Database Service - DEMO VERSION
 * 
 * Adapted for web-only demo mode
 * Always uses DemoStorageService (no Electron IPC)
 */

import demoStorageService from './demoStorageService';

const isElectron = () => {
  // Always false in demo mode
  return false;
};

// Always use demo storage in this version
const isDemoMode = () => true;

/**
 * Base database service that works with both Electron IPC and HTTP API
 */
class LocalDbService {
  /**
   * Get all entities of a given type
   * @param {string} entity - The entity type (e.g., 'clients', 'products')
   * @returns {Promise<Array>} - Array of entities
   */
  async getAll(entity) {
    // Always use demo storage
    return await demoStorageService.getAll(entity);
  }

  /**
   * Get a specific entity by ID
   * @param {string} entity - The entity type
   * @param {string} id - The entity ID
   * @returns {Promise<Object>} - The entity object
   */
  async get(entity, id) {
    // Always use demo storage
    return await demoStorageService.getById(entity, id);
  }

  /**
   * Create a new entity
   * @param {string} entity - The entity type
   * @param {Object} data - The entity data
   * @returns {Promise<Object>} - The created entity
   */
  async create(entity, data) {
    // Always use demo storage
    return await demoStorageService.create(entity, data);
  }

  /**
   * Update an existing entity
   * @param {string} entity - The entity type
   * @param {string} id - The entity ID
   * @param {Object} data - The updated entity data
   * @returns {Promise<Object>} - The updated entity
   */
  async update(entity, id, data) {
    // Always use demo storage
    return await demoStorageService.update(entity, id, data);
  }

  /**
   * Delete an entity
   * @param {string} entity - The entity type
   * @param {string} id - The entity ID
   * @returns {Promise<Object>} - Result of the deletion
   */
  async delete(entity, id) {
    // Always use demo storage
    return await demoStorageService.delete(entity, id);
  }

  /**
   * Get application settings
   * @param {string} key - Optional key for specific setting
   * @returns {Promise<Object|any>} - Settings object or specific setting value
   */
  async getSettings(key) {
    // Always use demo storage
    const settings = await demoStorageService.getSettings();
    return key ? settings[key] : settings;
  }

  /**
   * Update application settings
   * @param {string} key - Setting key
   * @param {any} value - Setting value
   * @returns {Promise<Object>} - Updated settings
   */
  async updateSetting(key, value) {
    // Always use demo storage
    const settings = await demoStorageService.getSettings();
    settings[key] = value;
    return await demoStorageService.updateSettings(settings);
  }

  /**
   * Update all application settings at once
   * @param {Object} settings - Complete settings object
   * @returns {Promise<Object>} - Updated settings
   */
  async updateSettings(settings) {
    // Always use demo storage
    return await demoStorageService.updateSettings(settings);
  }

  /**
   * Check if the database is accessible
   * @returns {Promise<boolean>} - True if database is accessible
   */
  async isAvailable() {
    try {
      await this.getSettings();
      return true;
    } catch (error) {
      console.error('Database not available:', error);
      return false;
    }
  }
}

// Export a singleton instance
const localDbService = new LocalDbService();
export default localDbService;